import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { z } from 'zod';

import { STORAGE_KEYS } from './storageKeys';

import { EditableQuizDetail } from '@/routes/TrainingPlans/BuildQuizzes/quizEditorTypes';

// Zod schema for answer validation - matches CustomQuizAnswerDefinitionDTO
// Use loose to allow any extra fields from the API
const QuizAnswerSchema = z
  .object({
    answer: z.record(z.string(), z.string()).optional(),
    correct: z.boolean().optional(),
  })
  .loose();

// Zod schema for question validation - matches CustomQuizQuestionDefinitionDTO
const QuizQuestionSchema = z
  .object({
    question: z.record(z.string(), z.string()).optional(),
    questionType: z.string().optional(),
    answers: z.array(QuizAnswerSchema).optional(),
  })
  .loose();

// Zod schema for quiz draft validation
// This is a loose validation - we just need to ensure basic structure exists
// Use loose to preserve any API fields we don't explicitly define
const QuizDraftSchema = z
  .object({
    // Quiz content - matches CustomQuizDetailDefinitionDTO structure
    ownerId: z.string(),
    name: z.record(z.string(), z.string()).optional(),
    passingGrade: z.union([z.number(), z.string()]).optional(), // Can be string from form input
    questions: z.array(QuizQuestionSchema).optional(),
    formId: z.string().optional(),
    quizId: z.string().optional(),

    // Metadata for restoration after Okta redirect
    existingQuizId: z.string().nullable().optional(),
    savedAt: z.number(),
    /** Wall-clock start for PATHWAY-3818 quiz build duration (create flow only). */
    buildStartedAt: z.number().optional(),

    // Flag to indicate a save was in progress when redirected
    pendingSave: z.boolean().optional(),
  })
  .loose(); // Allow any extra fields from the API DTO

// QuizDraft extends the API DTO with metadata fields for draft restoration
export type QuizDraft = EditableQuizDetail & {
  existingQuizId?: string | null;
  savedAt: number;
  buildStartedAt?: number;
  pendingSave?: boolean;
};

export const buildQuizWallClockDurationProps = (startedAt: number | null) => {
  if (startedAt === null) {
    return {};
  }

  const durationMs = Math.max(0, Date.now() - startedAt);
  return {
    duration_ms: durationMs,
    duration_seconds: Math.round(durationMs / 1000),
  };
};

/**
 * Drafts persisted before the `operatorId` → `ownerId` rename still sit in
 * users' localStorage. Map the old key on read so they keep restoring.
 */
const migrateLegacyDraft = (data: unknown): unknown => {
  if (
    typeof data !== 'object' ||
    data === null ||
    'ownerId' in data ||
    !('operatorId' in data)
  ) {
    return data;
  }
  const { operatorId, ...rest } = data;
  return { ...rest, ownerId: operatorId };
};

/**
 * Parse and validate a quiz draft from unknown data.
 * Returns the validated draft or null if invalid.
 */
const parseQuizDraft = (data: unknown): QuizDraft | null => {
  const result = QuizDraftSchema.safeParse(migrateLegacyDraft(data));
  if (result.success) {
    return result.data as QuizDraft;
  }
  console.warn('Invalid quiz draft data:', result.error.flatten());
  return null;
};

/**
 * Get the current pendingSave flag directly from localStorage.
 * This avoids race conditions with Jotai's async state updates.
 */
export const getPendingSaveFlagSync = (): boolean => {
  const raw = localStorage.getItem(STORAGE_KEYS.QUIZ_DRAFT);
  if (!raw) return false;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'pendingSave' in parsed) {
      return Boolean((parsed as { pendingSave?: boolean }).pendingSave);
    }
    return false;
  } catch (error) {
    console.warn(
      'Failed to parse quiz draft from localStorage when reading pendingSave flag:',
      STORAGE_KEYS.QUIZ_DRAFT,
      error,
    );
    return false;
  }
};

// Custom storage with Zod validation
const quizDraftStorage = createJSONStorage<QuizDraft | null>(
  () => localStorage,
);

const validatedStorage: typeof quizDraftStorage = {
  ...quizDraftStorage,
  getItem: (key: string, initialValue: QuizDraft | null) => {
    const raw = localStorage.getItem(key);
    if (!raw) return initialValue;

    try {
      const parsed = migrateLegacyDraft(JSON.parse(raw));

      // Handle explicit null (draft was cleared)
      if (parsed === null) {
        return initialValue;
      }

      const result = QuizDraftSchema.safeParse(parsed);

      if (result.success) {
        return result.data as QuizDraft;
      }

      // Validation failed but DON'T remove the data
      // The data might still be usable, just not matching our strict schema

      // Return the parsed data anyway if it has the minimum required fields
      const maybeValid = parsed as Record<string, unknown>;
      if (
        typeof maybeValid === 'object' &&
        'ownerId' in maybeValid &&
        'savedAt' in maybeValid
      ) {
        return parsed as QuizDraft;
      }

      return initialValue;
    } catch (e: unknown) {
      // JSON parse failed - this is actually corrupted
      localStorage.removeItem(key);
      console.warn('Corrupted quiz draft data removed:', e);
      return initialValue;
    }
  },
};

export const quizDraftAtom = atomWithStorage<QuizDraft | null>(
  STORAGE_KEYS.QUIZ_DRAFT,
  null,
  validatedStorage,
  { getOnInit: true },
);

/**
 * Whether BuildQuizzesView should hydrate from a stored draft instead of the
 * create location.state / API quiz details.
 *
 * Matches the original PATHWAY-2933 behavior: restore any matching edit draft
 * (refresh / return from the list / Okta) or an in-progress create draft.
 * Successful PUT / list rename clears the draft so GET wins.
 */
export const shouldRestoreQuizDraft = (
  draft: QuizDraft | null,
  options: { isEditMode: boolean; quizId?: string },
): draft is QuizDraft => {
  if (!draft) {
    return false;
  }

  if (options.isEditMode) {
    return draft.existingQuizId === options.quizId;
  }

  return !draft.existingQuizId;
};

/**
 * quizDraftAtom updater: return null when the one stored draft is for
 * `quizId`, otherwise leave it unchanged.
 *
 * There is only one slot. Matching (vs always null) exists for two tabs
 * sharing localStorage: tab 1 editing quiz A must keep its draft when tab 2
 * renames quiz B. If the renamed quiz is A, drop the draft so a refresh /
 * Okta return on that editor tab cannot hydrate the pre-rename name.
 */
export const clearMatchingQuizDraft = (
  draft: QuizDraft | null,
  quizId: string | undefined,
): QuizDraft | null => {
  if (!draft || !quizId) {
    return draft;
  }

  if (draft.existingQuizId === quizId || draft.quizId === quizId) {
    return null;
  }

  return draft;
};

/**
 * Synchronously set the pendingSave flag on the quiz draft in localStorage.
 * This is needed because Jotai's async state updates may not complete before
 * an Okta redirect happens during a save operation.
 */
export const setPendingSaveFlagSync = (pending: boolean): void => {
  const raw = localStorage.getItem(STORAGE_KEYS.QUIZ_DRAFT);
  if (!raw) return;

  try {
    const parsed: unknown = JSON.parse(raw);
    const draft = parseQuizDraft(parsed);

    if (draft) {
      draft.pendingSave = pending;
      localStorage.setItem(STORAGE_KEYS.QUIZ_DRAFT, JSON.stringify(draft));
    }
  } catch {
    // Ignore parse errors - draft may be invalid
  }
};
